import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { List, X, CaretDown, CaretRight, Scales } from '@phosphor-icons/react';
import { Button } from './ui/button';

const ServicesDropdown = ({ isAtTop }) => {
  const [activeCategory, setActiveCategory] = useState('trademark');
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsOpen(false), 300);
  };

  const serviceCategories = {
    trademark: {
      title: 'Trademark Services',
      description: 'Comprehensive trademark protection for your brand',
      services: [
        { name: 'Trademark Search & Registration', desc: 'Protect your brand name and logo', link: '/services/trademark/search-registration' },
        { name: 'Trademark Clearance Searches', desc: 'Verify availability before filing', link: '/services/trademark/clearance' },
        { name: 'Trademark Application Preparation', desc: 'Professional USPTO applications', link: '/services/trademark/application' },
        { name: 'Trademark Office Actions', desc: 'Respond to USPTO objections', link: '/services/trademark/office-actions' },
        { name: 'Trademark Monitoring', desc: 'Track potential infringements', link: '/services/trademark/monitoring' },
        { name: 'Statement Of Use Filings', desc: 'Complete your ITU application', link: '/services/trademark/statement-of-use' },
        { name: 'Trademark Change of Ownership', desc: 'Transfer trademark rights', link: '/services/trademark/ownership-change' },
        { name: 'Trademark Portfolio Management', desc: 'Manage multiple trademarks', link: '/services/trademark/portfolio' }
      ]
    },
    copyright: {
      title: 'Copyright Services',
      description: 'Protect your creative works and intellectual content',
      services: [
        { name: 'Copyright Registration', desc: 'Official U.S. Copyright Office filing', link: '/services/copyright/registration' },
        { name: 'Copyright Enforcement', desc: 'Stop unauthorized use', link: '/services/copyright/enforcement' },
        { name: 'Copyright Assignments & Licensing', desc: 'Transfer and license rights', link: '/services/copyright/assignments' },
        { name: 'Copyright Litigation', desc: 'Federal court representation', link: '/services/copyright/litigation' },
        { name: 'Copyright Portfolio Management', desc: 'Manage creative assets', link: '/services/copyright/portfolio' },
        { name: 'Digital Millennium Copyright Act', desc: 'DMCA takedown services', link: '/services/copyright/dmca' }
      ]
    },
    patent: {
      title: 'Patent Services',
      description: 'Secure your innovations and inventions',
      services: [
        { name: 'Patent Searches', desc: 'Prior art and patentability analysis', link: '/services/patent/searches' },
        { name: 'Office Actions & Amendments', desc: 'Overcome USPTO rejections', link: '/services/patent/office-actions' },
        { name: 'Patent Applications', desc: 'Utility, design, and provisional patents', link: '/services/patent/applications' },
        { name: 'Patent Enforcement', desc: 'Protect against infringement', link: '/services/patent/enforcement' }
      ]
    },
    company: {
      title: 'Company Formation',
      description: 'Establish your business structure properly',
      services: [
        { name: 'LLC Formation', desc: 'Limited liability protection', link: '/services/company/llc' },
        { name: 'C Corporation', desc: 'For growth and investment', link: '/services/company/c-corp' },
        { name: 'S Corporation', desc: 'Pass-through taxation benefits', link: '/services/company/s-corp' },
        { name: 'Non-Profit Organization', desc: 'Tax-exempt entity formation', link: '/services/company/non-profit' }
      ]
    }
  };

  return (
    <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button data-testid="services-dropdown-trigger" className="flex items-center text-slate-700 hover:text-[#044B3B] font-medium transition-colors">
        Services
        <CaretDown className={`ml-1 h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && <div className="absolute left-0 right-0 h-4 top-full" />}
      <div
        className={`fixed left-0 right-0 bg-white shadow-[0_8px_32px_rgba(0,0,0,0.08)] overflow-hidden z-50 border-t border-slate-200 transition-all duration-200 ${
          isOpen ? 'opacity-100 visible pointer-events-auto' : 'opacity-0 invisible pointer-events-none'
        } ${isAtTop ? 'top-[107px]' : 'top-[72px]'}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="max-w-7xl mx-auto flex">
          <div className="w-72 bg-slate-900 p-8">
            <div className="mb-6">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#D4AF37] mb-1">Our Practice</p>
              <div className="w-8 h-0.5 bg-[#D4AF37]"></div>
            </div>
            <div className="space-y-1">
              {Object.entries(serviceCategories).map(([key, category]) => (
                <button
                  key={key}
                  data-testid={`service-category-${key}`}
                  onMouseEnter={() => setActiveCategory(key)}
                  className={`w-full text-left px-4 py-3 rounded-md flex items-center gap-3 transition-all ${
                    activeCategory === key
                      ? 'bg-[#044B3B] text-white font-semibold'
                      : 'text-slate-300 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <span className="text-sm">{category.title}</span>
                  {activeCategory === key && <CaretRight className="h-4 w-4 ml-auto" />}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 p-10 bg-white">
            <div className="mb-6">
              <h2 className="text-2xl font-heading font-bold text-slate-900">
                {serviceCategories[activeCategory].title}
              </h2>
              <p className="text-slate-500 text-sm mt-1">
                {serviceCategories[activeCategory].description}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {serviceCategories[activeCategory].services.map((service, index) => (
                <Link
                  key={index}
                  to={service.link}
                  onClick={() => setIsOpen(false)}
                  data-testid={`service-link-${index}`}
                  className="flex items-start gap-3 p-4 rounded-md border border-slate-100 hover:border-[#044B3B]/30 hover:shadow-md transition-all group"
                >
                  <div className="w-8 h-8 bg-[#044B3B] rounded-md flex items-center justify-center flex-shrink-0 group-hover:bg-[#03392D] transition-colors">
                    <CaretRight className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-slate-800 group-hover:text-[#044B3B] leading-snug">
                      {service.name}
                    </h4>
                    <p className="text-xs text-slate-400 mt-0.5">{service.desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Navbar = ({ onOpenModal }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsAtTop(scrollTop === 0);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <nav data-testid="main-navbar" className={`${isAtTop ? 'mynav' : ''} fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200/50 shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-all duration-300 ease-in-out`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-[72px]">
          <Link to="/" className="flex items-center gap-2.5" data-testid="navbar-logo">
           
             <img src="/assets/img/brand/logo-dark.png" alt="Logo" className="h-10" />
          </Link>

          <div className="hidden lg-max:flex items-center gap-8">
            <Link to="/" className={`text-sm font-medium transition-colors ${isActive('/') ? 'text-[#044B3B]' : 'text-slate-600 hover:text-[#044B3B]'}`}>
              Home
            </Link>
            <Link to="/about" className={`text-sm font-medium transition-colors ${isActive('/about') ? 'text-[#044B3B]' : 'text-slate-600 hover:text-[#044B3B]'}`}>
              About
            </Link>
            <ServicesDropdown isAtTop={isAtTop} />
            <Link to="/blogs" className={`text-sm font-medium transition-colors ${isActive('/blogs') ? 'text-[#044B3B]' : 'text-slate-600 hover:text-[#044B3B]'}`}>
              Insights
            </Link>
            <Link to="/contact" className={`text-sm font-medium transition-colors ${isActive('/contact') ? 'text-[#044B3B]' : 'text-slate-600 hover:text-[#044B3B]'}`}>
              Contact
            </Link>
            <Button
              data-testid="navbar-consultation-btn"
              onClick={() => window.cd360Api && window.cd360Api.liveChat.showWebWidget()}
              className="bg-[#044B3B] hover:bg-[#03392D] text-white font-semibold text-sm px-5 py-2 rounded-md"
            >
              Free Consultation
            </Button>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="lg-max:hidden text-slate-700"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>

        {isMenuOpen && (
          <div className="lg-max:hidden pb-6 border-t border-slate-100 mt-2 pt-4">
            <div className="flex flex-col gap-4">
              <Link to="/" onClick={() => setIsMenuOpen(false)} className="text-slate-700 hover:text-[#044B3B] font-medium text-sm">Home</Link>
              <Link to="/about" onClick={() => setIsMenuOpen(false)} className="text-slate-700 hover:text-[#044B3B] font-medium text-sm">About</Link>
              <div>
                <button
                  onClick={() => setIsServicesOpen(!isServicesOpen)}
                  className="flex items-center text-slate-700 hover:text-[#044B3B] w-full font-medium text-sm"
                >
                  Services
                  <CaretDown className={`ml-1 h-4 w-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                {isServicesOpen && (
                  <div className="ml-4 mt-2 flex flex-col gap-2 bg-slate-50 p-4 rounded-lg mobilemenu max-h-[60vh] overflow-y-auto">
                    <div className="mb-2">
                      <p className="text-xs font-semibold text-[#044B3B] uppercase tracking-wider mb-2">Trademark Services</p>
                      <Link to="/services/trademark/search-registration" onClick={() => setIsMenuOpen(false)} className="block text-slate-600 hover:text-[#044B3B] text-sm py-1">Search & Registration</Link>
                      <Link to="/services/trademark/clearance" onClick={() => setIsMenuOpen(false)} className="block text-slate-600 hover:text-[#044B3B] text-sm py-1">Clearance Searches</Link>
                      <Link to="/services/trademark/application" onClick={() => setIsMenuOpen(false)} className="block text-slate-600 hover:text-[#044B3B] text-sm py-1">Application Preparation</Link>
                      <Link to="/services/trademark/office-actions" onClick={() => setIsMenuOpen(false)} className="block text-slate-600 hover:text-[#044B3B] text-sm py-1">Office Actions</Link>
                      <Link to="/services/trademark/monitoring" onClick={() => setIsMenuOpen(false)} className="block text-slate-600 hover:text-[#044B3B] text-sm py-1">Monitoring</Link>
                      <Link to="/services/trademark/statement-of-use" onClick={() => setIsMenuOpen(false)} className="block text-slate-600 hover:text-[#044B3B] text-sm py-1">Statement Of Use Filings</Link>
                      <Link to="/services/trademark/ownership-change" onClick={() => setIsMenuOpen(false)} className="block text-slate-600 hover:text-[#044B3B] text-sm py-1">Change of Ownership</Link>
                      <Link to="/services/trademark/portfolio" onClick={() => setIsMenuOpen(false)} className="block text-slate-600 hover:text-[#044B3B] text-sm py-1">Portfolio Management</Link>
                    </div>
                    <div className="mb-2">
                      <p className="text-xs font-semibold text-[#044B3B] uppercase tracking-wider mb-2">Copyright Services</p>
                      <Link to="/services/copyright/registration" onClick={() => setIsMenuOpen(false)} className="block text-slate-600 hover:text-[#044B3B] text-sm py-1">Registration</Link>
                      <Link to="/services/copyright/enforcement" onClick={() => setIsMenuOpen(false)} className="block text-slate-600 hover:text-[#044B3B] text-sm py-1">Enforcement</Link>
                      <Link to="/services/copyright/assignments" onClick={() => setIsMenuOpen(false)} className="block text-slate-600 hover:text-[#044B3B] text-sm py-1">Assignments & Licensing</Link>
                      <Link to="/services/copyright/litigation" onClick={() => setIsMenuOpen(false)} className="block text-slate-600 hover:text-[#044B3B] text-sm py-1">Litigation</Link>
                      <Link to="/services/copyright/portfolio" onClick={() => setIsMenuOpen(false)} className="block text-slate-600 hover:text-[#044B3B] text-sm py-1">Portfolio Management</Link>
                      <Link to="/services/copyright/dmca" onClick={() => setIsMenuOpen(false)} className="block text-slate-600 hover:text-[#044B3B] text-sm py-1">DMCA Takedown</Link>
                    </div>
                    <div className="mb-2">
                      <p className="text-xs font-semibold text-[#044B3B] uppercase tracking-wider mb-2">Patent Services</p>
                      <Link to="/services/patent/searches" onClick={() => setIsMenuOpen(false)} className="block text-slate-600 hover:text-[#044B3B] text-sm py-1">Patent Searches</Link>
                      <Link to="/services/patent/office-actions" onClick={() => setIsMenuOpen(false)} className="block text-slate-600 hover:text-[#044B3B] text-sm py-1">Office Actions & Amendments</Link>
                      <Link to="/services/patent/applications" onClick={() => setIsMenuOpen(false)} className="block text-slate-600 hover:text-[#044B3B] text-sm py-1">Applications</Link>
                      <Link to="/services/patent/enforcement" onClick={() => setIsMenuOpen(false)} className="block text-slate-600 hover:text-[#044B3B] text-sm py-1">Enforcement</Link>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-[#044B3B] uppercase tracking-wider mb-2">Company Formation</p>
                      <Link to="/services/company/llc" onClick={() => setIsMenuOpen(false)} className="block text-slate-600 hover:text-[#044B3B] text-sm py-1">LLC Formation</Link>
                      <Link to="/services/company/c-corp" onClick={() => setIsMenuOpen(false)} className="block text-slate-600 hover:text-[#044B3B] text-sm py-1">C Corporation</Link>
                      <Link to="/services/company/s-corp" onClick={() => setIsMenuOpen(false)} className="block text-slate-600 hover:text-[#044B3B] text-sm py-1">S Corporation</Link>
                      <Link to="/services/company/non-profit" onClick={() => setIsMenuOpen(false)} className="block text-slate-600 hover:text-[#044B3B] text-sm py-1">Non-Profit Organization</Link>
                    </div>
                  </div>
                )}
              </div>
              <Link to="/blogs" onClick={() => setIsMenuOpen(false)} className="text-slate-700 hover:text-[#044B3B] font-medium text-sm">Insights</Link>
              <Link to="/contact" onClick={() => setIsMenuOpen(false)} className="text-slate-700 hover:text-[#044B3B] font-medium text-sm">Contact</Link>
              <Button
                data-testid="mobile-consultation-btn"
                onClick={() => { setIsMenuOpen(false); window.cd360Api && window.cd360Api.liveChat.showWebWidget(); }}
                className="bg-[#044B3B] hover:bg-[#03392D] text-white font-semibold w-full text-sm rounded-md"
              >
                Free Consultation
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
